import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, RefreshCw, Users, Target, BookOpen, Mail, Archive, ArchiveRestore } from 'lucide-react';
import { fetchCentrosCatalog, archivarCentro, desarchivarCentro } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const fmt = (v, decimals = 0) => {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};

export function CentrosTab() {
  const [search, setSearch] = useState('');
  const qc = useQueryClient();

  // include_archived=true: la pestaña Admin necesita ver los archivados para
  // poder desarchivarlos — el resto de la app (selectores Asignar Sede, etc.)
  // usa fetchCentrosCatalog() sin este flag y los sigue excluyendo (04/09).
  const { data: centros = [], isLoading, refetch } = useQuery({
    queryKey: ['admin-centros-catalog', 'include-archived'],
    queryFn: () => fetchCentrosCatalog(undefined, true),
    staleTime: 5 * 60 * 1000,
  });

  const archiveMut = useMutation({
    mutationFn: ({ codigo, reason }) => archivarCentro(codigo, reason),
    onSuccess: (_res, { codigo }) => {
      toast.success(`${codigo} archivado — ya no cuenta en Mis Centros/Semáforo/KPIs`);
      qc.invalidateQueries({ queryKey: ['admin-centros-catalog'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error al archivar'),
  });

  const unarchiveMut = useMutation({
    mutationFn: (codigo) => desarchivarCentro(codigo),
    onSuccess: (_res, codigo) => {
      toast.success(`${codigo} reactivado`);
      qc.invalidateQueries({ queryKey: ['admin-centros-catalog'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error al desarchivar'),
  });

  const handleArchive = (c) => {
    const reason = window.prompt(`Archivar "${c.nombre || c.codigo}" (${c.codigo}) — motivo (opcional, ej. "Contrato finalizado"):`, 'Contrato finalizado');
    if (reason === null) return; // cancelado
    archiveMut.mutate({ codigo: c.codigo, reason });
  };

  const filtered = centros.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.codigo || '').toLowerCase().includes(q) ||
      (c.nombre || '').toLowerCase().includes(q) ||
      (c.jefe_centro || '').toLowerCase().includes(q)
    );
  });

  // Summary stats
  const totalOpsObj = centros.reduce((s, c) => s + (c.ops_objetivo || 0), 0);
  const totalCharlasObj = centros.reduce((s, c) => s + (c.charlas_estimacion || 0), 0);
  const totalPlantilla = centros.reduce((s, c) => s + (c.plantilla || 0), 0);
  const conJefe = centros.filter((c) => c.jefe_centro).length;
  const archivados = centros.filter((c) => c.archived).length;

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl border" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
          <p className="text-[10px] uppercase tracking-wider font-montserrat mb-1 flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
            <BarChart3 size={10} /> Total Centros
          </p>
          <p className="text-2xl font-bold text-white">{isLoading ? '—' : centros.length.toLocaleString('es-ES')}</p>
          {archivados > 0 && (
            <p className="text-[9px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{archivados} archivado{archivados !== 1 ? 's' : ''}</p>
          )}
        </div>
        <div className="p-4 rounded-xl border" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
          <p className="text-[10px] uppercase tracking-wider font-montserrat mb-0.5 flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
            <Target size={10} /> OPS Objetivo/mes
          </p>
          <p className="text-2xl font-bold" style={{ color: '#4A9EFF' }}>{isLoading ? '—' : fmt(totalOpsObj)}</p>
        </div>
        <div className="p-4 rounded-xl border" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
          <p className="text-[10px] uppercase tracking-wider font-montserrat mb-0.5 flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
            <BookOpen size={10} /> Charlas/mes <span className="normal-case text-[9px]">(est.)</span>
          </p>
          <p className="text-2xl font-bold" style={{ color: '#00C853' }}>{isLoading ? '—' : fmt(totalCharlasObj)}</p>
        </div>
        <div className="p-4 rounded-xl border" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
          <p className="text-[10px] uppercase tracking-wider font-montserrat mb-0.5 flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
            <Users size={10} /> Plantilla total
          </p>
          <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{isLoading ? '—' : fmt(totalPlantilla)}</p>
          <p className="text-[9px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{conJefe} con jefe asignado</p>
        </div>
      </div>

      {/* Search + table */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Input
            placeholder="Buscar por código, nombre o jefe de centro…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 h-8 text-xs border"
            style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
            data-testid="centros-search-input"
          />
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => refetch()} style={{ color: 'var(--vz-text-secondary)' }} data-testid="centros-refresh-button">
            <RefreshCw size={12} /> Actualizar
          </Button>
        </div>

        <ScrollArea className="h-96">
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                {[
                  { label: 'Código', w: 'w-[90px]' },
                  { label: 'Centro de Trabajo', w: 'w-[200px]' },
                  { label: 'OPS/mes', w: 'w-[80px]' },
                  { label: 'Charlas/mes', w: 'w-[110px]' },
                  { label: 'Plantilla', w: 'w-[80px]' },
                  { label: 'Jefe de Centro', w: '' },
                  { label: 'Contrato', w: 'w-[140px]' },
                ].map((h) => (
                  <TableHead key={h.label} className={`text-[10px] uppercase tracking-wider font-montserrat ${h.w}`} style={{ color: 'var(--vz-text-muted)' }}>
                    {h.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-3" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>
                    {search ? 'Sin resultados para esa búsqueda' : 'No hay centros importados.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow key={c.codigo} className="hover:bg-[#132860] transition-colors duration-200" style={{ borderColor: 'var(--vz-border-subtle)', opacity: c.archived ? 0.55 : 1 }} data-testid={`centro-row-${c.codigo}`}>
                    <TableCell className="text-xs font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                      {c.codigo}
                    </TableCell>
                    <TableCell className="text-xs font-medium text-white max-w-[200px]">
                      <span className="block truncate" title={c.nombre}>{c.nombre || '—'}</span>
                    </TableCell>
                    <TableCell>
                      {c.ops_objetivo != null ? (
                        <Badge className="text-[10px] font-mono" style={{ background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.25)' }}>
                          {fmt(c.ops_objetivo)}
                        </Badge>
                      ) : <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        {c.charlas_estimacion != null ? (
                          <Badge className="text-[10px] font-mono w-fit" style={{ background: 'rgba(0,200,83,0.1)', color: '#00C853', border: '1px solid rgba(0,200,83,0.25)' }}>
                            {fmt(c.charlas_estimacion)}
                          </Badge>
                        ) : <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                        <span className="text-[9px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>estimación mensual</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.plantilla != null ? (
                        <Badge className="text-[10px] font-mono" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.25)' }}>
                          {fmt(c.plantilla, 1)}
                        </Badge>
                      ) : <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                    </TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>
                      {c.jefe_centro ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-white">{c.jefe_centro}</span>
                          {c.correo_jefe && (
                            <a
                              href={`mailto:${c.correo_jefe}`}
                              className="text-[10px] flex items-center gap-1 hover:underline"
                              style={{ color: '#4A9EFF' }}
                            >
                              <Mail size={9} />{c.correo_jefe}
                            </a>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                    </TableCell>
                    <TableCell>
                      {c.archived ? (
                        <div className="flex items-center gap-1.5">
                          <Badge className="text-[10px]" style={{ background: 'rgba(148,163,184,0.15)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.3)' }} data-testid={`centro-archived-badge-${c.codigo}`}>
                            Archivado
                          </Badge>
                          <Button
                            size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] gap-1"
                            disabled={unarchiveMut.isPending}
                            onClick={() => unarchiveMut.mutate(c.codigo)}
                            data-testid={`centro-unarchive-btn-${c.codigo}`}
                            title="Reactivar contrato"
                          >
                            <ArchiveRestore size={11} />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] gap-1"
                          disabled={archiveMut.isPending}
                          onClick={() => handleArchive(c)}
                          data-testid={`centro-archive-btn-${c.codigo}`}
                          style={{ color: 'var(--vz-text-muted)' }}
                          title="Archivar contrato"
                        >
                          <Archive size={11} /> Archivar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
        {filtered.length > 0 && (
          <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
            {filtered.length} centros{search ? ` (filtrados de ${centros.length})` : ''} · Datos actualizados: {centros[0]?.updated_at ? new Date(centros[0].updated_at).toLocaleDateString('es-ES') : '—'}
          </p>
        )}
      </div>
    </div>
  );
}
